import { useCallback, useMemo } from 'react';
import {
  Typography, Stack, Link as MuiLink, Container,
  Grid, IconButton,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ShareIcon from '@mui/icons-material/Share';
import { faSpotify } from '@fortawesome/free-brands-svg-icons';
import { RoundedMedia } from '@client/components/common/Media';
import { getDateRange } from '@sda/utils/date';
import { ProfileTypes } from '@sda/enums';
import { getExternalUrl } from '@sda/utils/url';
import { MediaEmbed } from '@client/components/common/MediaEmbed';
import { DocumentTitle } from '@client/components/common/DocumentTitle';
import { getCoverImage, getImageLargeUrl } from '@sda/utils/image';
import ActPlaceholderImage from '@client/images/placeholders/artist-small.jpg';
import VenuePlaceholderImage from '@client/images/placeholders/venue-small.jpg';
import { Modal } from '@client/components/globals/Modal';
import { Profile } from '@client/components/Main/Profiles/Profile';
import { SiteContainer } from '@client/components/common/SiteContainer';
import { getNormalizedProfile, getProfileRespondsQuickly } from '@sda/utils/meta';
import { Description } from '@client/components/common/Description';
import { getEventLocation, getRatingString, getRoundedFollowers } from '@sda/utils/string';
import { useProfileLink } from '@client/components/Main/Profiles/useProfileLink.hook';
import { FeaturedReview } from '@client/components/common/Review/FeaturedReview';
import { GenreLabels } from '@client/components/Main/Profiles/GenreLabels';
import { VenueTypeLabel } from '@client/components/Main/Profiles/VenueTypeLabel';
import { FontAwesomeIcon } from '@client/components/common/FontAwesomeIcon';
import { HomeSection } from '@client/components/common/HomeSection';
import { HostMap } from '@client/components/Main/Profiles/Venue/HostMap';
import { useSelector } from '@client/hooks/redux.hooks';
import { getUserId } from '@client/selectors/helpers/login';
import { CTABar } from './CTABar';
import { useShareShowCall } from './useShareShowCall.hook';
import type { Beacon } from '@sda/interfaces/beacon';
import type { FetchDataType } from '@sda/sdk/interfaces';

const useStyles = makeStyles()((theme) => ({
  root: {
    height: '100%',
    flex: 1,

    [theme.breakpoints.up('md')]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  titleContainer: {
    '& > span, * > svg': {
      verticalAlign: 'middle',
    },
  },
  titleIcon: {
    fontSize: 18,
    marginRight: theme.spacing(1 / 2),
  },
  requirements: {
    '& > svg': {
      marginRight: theme.spacing(1),
      fontSize: 20,
    },

    '& > svg, & > span': {
      verticalAlign: 'middle',
    },
  },
  websiteUrlLink: {
    color: `${theme.palette.primary.main} !important`,
  },
  listItemProfile: {
    display: 'inline-flex',
    paddingBottom: 0,
    paddingTop: 0,
    width: 'auto',

    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  sticky: {
    position: 'sticky',
    top: theme.spacing(3),
    marginTop: theme.spacing(8 * -1),

    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(7 * -1),
    },
  },
  main: {
    maxWidth: theme.breakpoints.values.narrow,
  },
  featuredReview: {
    backgroundColor: theme.palette.grey[50],
  },
  profile: {
    backgroundColor: theme.palette.grey[200],
  },
  profileContainer: {
    padding: theme.spacing(3, 2),

    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3, 0, 4, 0),
    },
  },
}));

interface ShowCallProps {
  readonly preview?: boolean;
}

export const ShowCall: React.FC<ShowCallProps & Beacon & FetchDataType> = ({ preview = false, ...call }) => {
  const { classes } = useStyles();
  const userId = useSelector(getUserId);

  const profileRespondsQuickly = useMemo(() => getProfileRespondsQuickly(call.profileData), [call.profileData]);

  let location = getEventLocation(call);

  if (!location && call.compiledAddress) {
    const parts = call.compiledAddress.split(',') || [];

    location = parts[0] || '';
  }

  const handleShareButtonClick = useShareShowCall(call.id);

  const handleProfileButtonClick = useCallback(() => {
    Modal.show({
      title: 'View Profile',
      fullScreen: true,
      maxWidth: 'narrow',
      buttons: [],
      scroll: 'body',
      body: (
        <SiteContainer
          maxWidth="narrow"
          disableGutters
        >
          <Profile
            {...getNormalizedProfile(call.profileType, { ...call.profileData, id: call.profileId })}
          />
        </SiteContainer>
      ),
    });
  }, [call]);

  const profileLinkProps = useProfileLink(call.profileType, call.profileData, handleProfileButtonClick);
  const hasGenre = Boolean(call.profileData.primaryGenre) || Boolean(call.profileData.secondaryGenre);

  return (
    <>
      <DocumentTitle
        title={call.profileData.name}
        description={call.description}
        image={getCoverImage(call.profileData.publicImageUrl)}
      />

      {!preview && (
        <CTABar {...call} />
      )}

      <div>
        <Container
          maxWidth="narrow"
          className={classes.root}
        >
          <RoundedMedia
            title={call.profileData.name}
            images={call.profileData.publicImageUrl}
            loading="eager"
            sizeCallback={getImageLargeUrl}
            fallback={call.profileType === ProfileTypes.ACT ? ActPlaceholderImage : VenuePlaceholderImage}
            badgeId={call.profileData.badge}
            isFeatured={call.isFeatured}
            enableCarousel
          />

          <Stack spacing={3}>
            <Stack spacing={2}>
              <div>
                <Typography
                  variant="h6"
                  className={classes.titleContainer}
                >
                  {(call.owner === userId || Boolean(preview)) && !call.isPublicCall && (
                    <VisibilityOffIcon className={classes.titleIcon} />
                  )}

                  <span>{call.profileData.name}</span>
                </Typography>

                {Boolean(hasGenre) && (
                  <GenreLabels
                    primaryGenre={call.profileData.primaryGenre}
                    secondaryGenre={call.profileData.secondaryGenre}
                  />
                )}

                {Boolean(call.profileData.venueType) && (
                  <VenueTypeLabel
                    venueType={call.profileData.venueType}
                    paragraph
                  />
                )}
              </div>

              <div>
                {call.startDate ? (
                  <Typography
                    variant="body2"
                    paragraph
                  >
                    {getDateRange(call)}
                  </Typography>
                ) : (
                  <Typography
                    color="disabled"
                    component="span"
                    display="inline"
                    paragraph
                  >
                    Date
                  </Typography>
                )}

                <Typography
                  variant="body1"
                >
                  {location || (
                    <Typography
                      color="disabled"
                      component="span"
                      display="inline"
                    >
                      Location
                    </Typography>
                  )}
                </Typography>
              </div>

              <Description
                variant="subtitle1"
                color={call.description ? 'textPrimary' : 'disabled'}
              >
                {call.description || 'Description'}
              </Description>

              <div>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.requirements}
                  paragraph
                >
                  <FavoriteIcon color="error" />

                  <span>
                    {getRatingString(call.profileData.score, call.profileData.numReviews)}
                  </span>
                </Typography>

                {call.profileData.spotifyMonthlyActiveListeners > 0 && (
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classes.requirements}
                    paragraph
                  >
                    <FontAwesomeIcon
                      icon={faSpotify}
                      color="success"
                    />

                    <span>
                      <MuiLink
                        id="sda-showcall-spotify-link"
                        href={call.profileData.spotifyArtistUrl}
                        target="_blank"
                        rel="noreferrer"
                        color="textSecondary"
                        title={`${call.profileData.name} Spotify profile`}
                      >
                        {getRoundedFollowers(call.profileData.spotifyMonthlyActiveListeners)}

                        {' '}
                        monthly listeners
                      </MuiLink>
                    </span>
                  </Typography>
                )}

                {(call.profileData.hasPA || call.profileData.requiresPA) && (
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classes.requirements}
                    paragraph
                  >
                    <VolumeUpIcon color="warning" />

                    <span>
                      {call.profileType === ProfileTypes.ACT ? 'Requires a PA system' : 'Has a PA system'}
                    </span>
                  </Typography>
                )}

                {Boolean(profileRespondsQuickly) && (
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classes.requirements}
                  >
                    <ScheduleIcon color="info" />

                    <span>
                      Responds quickly
                    </span>
                  </Typography>
                )}
              </div>
            </Stack>
          </Stack>
        </Container>
      </div>

      <HomeSection
        maxWidth="narrow"
        rootClassName={classes.profile}
        className={classes.profileContainer}
      >
        <Stack spacing={2}>
          <div>
            <Grid
              container
              wrap="nowrap"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid
                item
                zeroMinWidth
              >
                <MuiLink
                  id="sda-view-profile-button"
                  component="button"
                  aria-label="view profile"
                  variant="subtitle2"
                  color="textPrimary"
                  className="break-word"
                  underline="hover"
                  {...profileLinkProps}
                >
                  About
                  {' '}

                  {call.profileData.name}
                </MuiLink>
              </Grid>

              <Grid item>
                <IconButton
                  edge="end"
                  aria-label="share"
                  onClick={handleShareButtonClick}
                  color="inherit"
                  size="small"
                >
                  <ShareIcon />
                </IconButton>
              </Grid>
            </Grid>
          </div>

          {Boolean(call.profileData.bioHTML || call.profileData.description) && (
            <Description variant="body1">
              {call.profileData.bioHTML || call.profileData.description}
            </Description>
          )}

          {Boolean(call.profileData.websiteUrl) && (
            <MuiLink
              id="sda-call-website-link"
              href={getExternalUrl(call.profileData.websiteUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.websiteUrlLink}
            >
              Website
            </MuiLink>
          )}

          {call.profileType === ProfileTypes.VENUE && (
            <HostMap
              location={call.profileData.fuzzyLatLng}
            />
          )}

          {Boolean(call.profileData.featuredReviewId) && (
            <FeaturedReview
              disableProfileClick={preview}
              reviewId={call.profileData.featuredReviewId}
              className={classes.featuredReview}
            />
          )}

          <MediaEmbed
            mediaType="video"
            {...call.profileData}
          />

          <MediaEmbed
            mediaType="audio"
            {...call.profileData}
          />
        </Stack>
      </HomeSection>
    </>
  );
};
